import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import useXHR from '../hooks/useXHR';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const COLORS = ['#FDE047', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#FDE68A', '#14b8a6', '#F59E0B'];

const AdvancedAnalyticsDashboardPage = ({ showToast }) => {
  const { token } = useAuth();
  const { get } = useXHR(token);
  const [timeRange, setTimeRange] = useState(30);
  const [trends, setTrends] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [catPopularity, setCatPopularity] = useState([]);
  const [catGrowth, setCatGrowth] = useState({ growth: [], categories: [] });
  const [loading, setLoading] = useState(false);

  const loadData = () => {
    setLoading(true);
    let done = 0;
    const onDone = () => { done++; if (done >= 4) setLoading(false); };

    get(`/advanced-analytics/search-trends?days=${timeRange}`, { onSuccess: (d) => setTrends(d.trends || []), onFinally: onDone });
    get(`/advanced-analytics/engagement-metrics?days=${timeRange}`, { onSuccess: (d) => setEngagement(d), onFinally: onDone });
    get(`/advanced-analytics/category-popularity?days=${timeRange}&limit=15`, { onSuccess: (d) => setCatPopularity(d.categories || []), onFinally: onDone });
    get(`/advanced-analytics/category-growth?days=${Math.max(timeRange, 90)}`, { onSuccess: (d) => setCatGrowth(d), onFinally: onDone });
  };

  useEffect(() => { if (!token) return; loadData(); }, [token, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const StatCard = ({ label, value, color, icon }) => (
    <div style={{
      background: `linear-gradient(135deg, ${color}15, ${color}08)`,
      border: `1px solid ${color}30`,
      borderRadius: 14, padding: '18px 20px', flex: '1 1 180px', minWidth: 160
    }} data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ color: '#71717a', fontSize: '0.72rem', marginBottom: 4 }}>
        <i className={`fas ${icon}`} style={{ marginRight: 6, color }} />{label}
      </div>
      <div style={{ color: '#334155', fontSize: '1.6rem', fontWeight: 800 }}>{value}</div>
    </div>
  );

  return (
    <div className="card" data-testid="advanced-analytics-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, color: '#c4b5fd', display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fas fa-chart-line" style={{ color: '#FDE047' }} />
          Advanced Analytics Dashboard
        </h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90, 365].map(d => (
            <button key={d} onClick={() => setTimeRange(d)}
              data-testid={`range-${d}`}
              style={{
                background: timeRange === d ? 'rgba(253, 224, 71,0.3)' : 'rgba(255, 255, 255, 0.85)',
                border: `1px solid ${timeRange === d ? 'rgba(253, 224, 71,0.6)' : 'rgba(255,255,255,0.1)'}`,
                color: timeRange === d ? '#047857' : '#71717a',
                padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600
              }}>
              {d}d
            </button>
          ))}
          <button onClick={loadData} data-testid="refresh-analytics"
            style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            <i className="fas fa-sync-alt" style={{ marginRight: 4 }} />{loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#71717a' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', marginBottom: 10 }} />
          <p>Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          {engagement && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }} data-testid="key-metrics">
              <StatCard label="Total URLs" value={engagement.total_results} color="#FDE047" icon="fa-link" />
              <StatCard label={`New (${timeRange}d)`} value={engagement.recent_results} color="#10b981" icon="fa-plus-circle" />
              <StatCard label="With Reactions" value={engagement.with_reactions} color="#f59e0b" icon="fa-heart" />
              <StatCard label="Geolocated" value={engagement.with_location} color="#3b82f6" icon="fa-map-marker-alt" />
              <StatCard label="Categories" value={engagement.total_categories} color="#FDE68A" icon="fa-tags" />
            </div>
          )}

          {/* Search Trends Chart */}
          <div style={{ background: '#475569', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid rgba(253, 224, 71,0.15)' }} data-testid="search-trends-chart">
            <h3 style={{ color: '#047857', fontSize: '0.95rem', marginBottom: 14 }}>
              <i className="fas fa-chart-area" style={{ marginRight: 8 }} />Search Activity Trend
            </h3>
            {trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717a' }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#71717a' }} />
                  <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid #FDE04740', borderRadius: 8, color: '#334155' }} />
                  <Area type="monotone" dataKey="count" stroke="#FDE047" fill="rgba(253, 224, 71,0.15)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p style={{ color: '#555', textAlign: 'center' }}>No search data in this period.</p>}
          </div>

          {/* Two Column Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, marginBottom: 20 }}>
            {/* Category Popularity */}
            <div style={{ background: '#475569', borderRadius: 14, padding: 20, border: '1px solid rgba(16,185,129,0.15)' }} data-testid="category-popularity-chart">
              <h3 style={{ color: '#10b981', fontSize: '0.95rem', marginBottom: 14 }}>
                <i className="fas fa-fire" style={{ marginRight: 8 }} />Category Popularity
              </h3>
              {catPopularity.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={catPopularity.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#71717a' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#a1a1aa' }} width={120} />
                    <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid #10b98140', borderRadius: 8, color: '#334155' }} />
                    <Bar dataKey="count" fill="#10b981" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p style={{ color: '#555', textAlign: 'center' }}>No category data yet.</p>}
            </div>

            {/* Article Type Distribution */}
            {engagement?.article_types?.length > 0 && (
              <div style={{ background: '#475569', borderRadius: 14, padding: 20, border: '1px solid rgba(253, 230, 138,0.15)' }} data-testid="article-types-chart">
                <h3 style={{ color: '#047857', fontSize: '0.95rem', marginBottom: 14 }}>
                  <i className="fas fa-file-alt" style={{ marginRight: 8 }} />Content Type Distribution
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={engagement.article_types} dataKey="count" nameKey="type" cx="50%" cy="50%"
                      outerRadius={100} innerRadius={50} label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#555' }}>
                      {engagement.article_types.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid #FDE68A40', borderRadius: 8, color: '#334155' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Category Growth Over Time */}
          {catGrowth.growth.length > 0 && (
            <div style={{ background: '#475569', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid rgba(245, 158, 11,0.15)' }} data-testid="category-growth-chart">
              <h3 style={{ color: '#60a5fa', fontSize: '0.95rem', marginBottom: 14 }}>
                <i className="fas fa-seedling" style={{ marginRight: 8 }} />Category Growth (Weekly)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={catGrowth.growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#71717a' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#71717a' }} />
                  <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid #3b82f640', borderRadius: 8, color: '#334155' }} />
                  {catGrowth.categories.map((cat, i) => (
                    <Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                {catGrowth.categories.map((cat, i) => (
                  <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', color: '#a1a1aa' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length], display: 'inline-block' }} />
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Two Column: Top Domains + Hourly Activity */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20 }}>
            {/* Top Domains */}
            {engagement?.top_domains?.length > 0 && (
              <div style={{ background: '#475569', borderRadius: 14, padding: 20, border: '1px solid rgba(245,158,11,0.15)' }} data-testid="top-domains">
                <h3 style={{ color: '#f59e0b', fontSize: '0.95rem', marginBottom: 14 }}>
                  <i className="fas fa-globe" style={{ marginRight: 8 }} />Top Domains
                </h3>
                {engagement.top_domains.map((d, i) => (
                  <div key={d.domain} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#555', fontSize: '0.7rem', width: 20, textAlign: 'right' }}>{i + 1}.</span>
                      <span style={{ color: '#334155', fontSize: '0.8rem' }}>{d.domain}</span>
                    </div>
                    <span style={{ color: '#f59e0b', fontSize: '0.75rem', fontWeight: 700 }}>{d.count}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Hourly Activity */}
            {engagement?.hourly_activity && (
              <div style={{ background: '#475569', borderRadius: 14, padding: 20, border: '1px solid rgba(20,184,166,0.15)' }} data-testid="hourly-activity">
                <h3 style={{ color: '#14b8a6', fontSize: '0.95rem', marginBottom: 14 }}>
                  <i className="fas fa-clock" style={{ marginRight: 8 }} />Activity by Hour (UTC)
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={engagement.hourly_activity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                    <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#71717a' }} />
                    <YAxis tick={{ fontSize: 9, fill: '#71717a' }} />
                    <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid #14b8a640', borderRadius: 8, color: '#334155' }} formatter={(v) => [v, 'Searches']} />
                    <Bar dataKey="count" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancedAnalyticsDashboardPage;
